/**
 * @file AttachmentList.tsx
 * @description The attachments panel's file rows and their expandable version
 * history. Consumer: `Attachments`.
 *
 * R18 once withheld every version with `kind === 'save'`, which hid a desktop
 * user's whole working history. That rule is gone: the history lists every
 * version the artifact map holds, save or published. Publishing no longer
 * governs what is visible, only whether a version can be shared or emailed.
 *
 * The artifact map is not a complete history — a winning save deletes its
 * sibling saves from it and a losing save never lands there — so an expanded
 * strip lists what `useAttachmentVersionHistory` returns, which merges the
 * attachment document's own versions under the map. A collapsed file is never
 * read for, so its tag claims no total at all until it has been (SYD-2455).
 *
 * A listed version the artifact map does not hold is passed down as such: the
 * row hands `AttachmentListActions` the resolved version and says whether the
 * map holds it, so an action that resolves against the map is disabled with a
 * reason rather than enabled and then failing. The four Extract schedule props
 * pass through to every `AttachmentListActions` unread.
 *
 * Rows lead with the file changed most recently inside each importance group,
 * and the date a row prints is the date it is ordered by: both come from
 * `resolveAttachmentChangedAt` over the version that row lists, never from the
 * artifact entry's `meta`, which only a rename rewrites (SYD-2972).
 *
 * The file row never tags a conflict, because the current version is never a
 * conflicted one: `commitAttachmentVersion` stamps `conflict` only on a version
 * that did not become current, and the shared `promotableVersionId` in
 * `types/src` skips conflicted versions when a deletion moves the pointer. Since
 * SYD-2478 that second half is a backend guarantee too — `removeAttachmentVersion`
 * applies the same rule inside its transaction. `AttachmentVersionRow` is where
 * a conflicted version appears.
 *
 * A file with a transfer shows `AttachmentTransferRow` in place of its normal
 * row, and a transfer for a file that has no row yet — a brand-new upload — is
 * listed above every file row. A file may hold more than one transfer at once,
 * because a batch containing two files of the same name resolves both against
 * the same attachment, so every transfer a file owns gets a row of its own; a
 * last-writer-wins map would leave one of them invisible and uncancellable. A
 * failed transfer holds that place until the reader retries or dismisses it.
 * The list reads only each transfer's `fileUid`, never its byte counts and never
 * its failure, so a progress tick or a failure re-renders the one row that reads
 * it and never the list.
 *
 * A file row is also a drop target: dropping a file on it adds that file as the
 * row's next version rather than as a new attachment. The row only claims the
 * pointer — `dropTargetFileUid` names the row currently under it, and
 * `onRowDragEnter`/`onRowDragLeave` are `useAttachmentPanelDrop`'s, which counts
 * crossed children so a move onto a child does not release the claim. A row
 * showing an `AttachmentTransferRow` registers no handler, so a drop over an
 * upload in flight falls through to the panel and attaches a new file.
 *
 * The retention sentence at the foot of an expanded strip is absent far more
 * often than present: the org age policy it describes is opt-in, so
 * `resolveAttachmentRetentionSentence` returns null until someone configures
 * one, and it describes what happens to saves, so a strip listing none does not
 * print it. It is read once for the whole list, not once per file, because the
 * policy is organization-wide.
 *
 * Each row carries an `AttachmentFileKeepToggle`, placed after the publish
 * button so it lands in the group that flows past the 300/75/200 px fixed
 * columns rather than being squeezed by them in the 500 px sidebar drawer. Only
 * the folded pin path is computed here — the artifact path this panel reads is
 * not one a pin may name, and `resolveDesktopPinPath` is the one place that is
 * expressed. The toggle subscribes to the pin map itself, so a pin write
 * re-renders the toggles alone. It is absent in the PDF export portal: a
 * control printed onto a rendered document is a defect, not a disabled state
 * (SYD-2455).
 *
 * The Syncretic File Sync nudge is shown on at most one row: the first file over
 * `LARGE_ATTACHMENT_ADVISORY_BYTES`. A document with five large files would
 * otherwise print five identical lines, and the design asks for one. Every
 * other condition that silences it — an existing pin, a dismissal, an
 * unconfigured download URL — lives inside `AttachmentSyncNudge`, so this list
 * subscribes to none of them. It too is absent in the export portal, where a
 * suggestion to install something reads as advertising.
 *
 * The file-type icon resolves from `sourceFileName`, not `displayFileName`:
 * `resolveAttachmentDisplayName` rewrites a raw email source file to a friendly
 * name that carries no extension, and the extension is what decides the type.
 * `useAttachmentLabels` is read once for the whole list rather than per row —
 * the vocabulary is organization-wide — and adds no listener, joining the
 * refcounted organization-parameters store every form page already holds. The
 * label picker stacks no debounce over `onUpdateFileLabels`, unlike the notes
 * and importance controls beside it: a toggle is one click, and a second window
 * only widens the gap in which an immediate write cancels a pending one.
 *
 * Both read `usePdfExportContext` here rather than being lifted to
 * `AttachmentsUI` the way the capacity caption was: they sit inside a file row,
 * which is not a place a parent can gate from.
 *
 * Both are also absent without the `fileSync` right, read once at the top of the
 * component. Syncretic File Sync is unreleased, so hiding rather than disabling
 * is deliberate: a disabled toggle would advertise a capability nobody can
 * obtain. The provenance surfaces beside them — the version origin tag and the
 * "Synced" chip — are not gated, because they describe versions a colleague's
 * agent may already have written into a shared document.
 */

import {
  CSSProperties,
  DragEvent,
  FC,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSnapshot } from 'valtio';
import { debounce } from 'lodash';
import {
  Button,
  Typography,
  theme,
  Tag,
  Input,
  Select,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { UploadFile } from 'antd/es/upload/interface';

import { ArtifactAttachments, AttachmentVersion } from '#types/System.types';
import { LARGE_ATTACHMENT_ADVISORY_BYTES, type LabelId } from '#types';
import {
  MAX_LIMITED_WIDTH_VIEWPORT,
  ImportanceOptions,
  formVersionNumberPrefix,
} from '#types/System.Parameters.types';
import type { RawEmailAliasContext } from './Attachments';
import type { ExtractScheduleHost } from './index.types';
import FlexBox from '../../atoms/FlexBox';
import { FORM_PAGE_CSS_VARS } from '../../atoms/FORM_PAGE_CSS_VARS';
import FlexCol from '../../atoms/FlexCol';
import { AttachmentListActions } from './AttachmentListActions';
import {
  resolveAttachmentDisplayName,
  resolveCurrentAttachmentName,
} from './resolveAttachmentDisplayName';
import {
  attachmentVersionOriginLabels,
  resolveAttachmentVersionOrigin,
} from './resolveAttachmentVersionOrigin';
import { resolveAttachmentChangedAt } from './resolveAttachmentChangedAt';
import { resolveAttachmentSyncChip } from './resolveAttachmentSyncChip';
import { resolveAttachmentRetentionSentence } from './resolveAttachmentRetentionSentence';
import { AttachmentVersionRow } from './AttachmentVersionRow';
import { AttachmentTransferRow } from './AttachmentTransferRow';
import { AttachmentFileKeepToggle } from './AttachmentFileKeepToggle';
import { AttachmentSyncNudge } from './AttachmentSyncNudge';
import { AttachmentFileTypeIcon } from './AttachmentFileTypeIcon';
import { AttachmentLabelPicker } from './AttachmentLabelPicker';
import { LabelTags } from '../../atoms/LabelTags';
import { useAttachmentLabels } from '../../../use/useOrganizationParameters';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { resolveDesktopPinPath } from '../../../use/desktopSync/resolveDesktopPinPath';
import { useFileSyncAccess } from '../../../use/desktopSync/useFileSyncAccess';
import { attachmentTransfersState } from '../../../store/attachmentTransfersStore';
import { formatBytes } from './formatBytes';
import { formatAttachmentDate } from './formatAttachmentDate';
import { useAttachmentSaveRetentionDays } from '../../../use/attachments/useAttachmentSaveRetentionDays';
import { useAttachmentVersionHistory } from '../../../use/attachments/useAttachmentVersionHistory';

const { Text } = Typography;
const { useToken } = theme;

/**
 * Off-screen but in the accessibility tree. The version tag's meaning used to
 * ride on an `aria-label`, which ARIA prohibits on the generic role antd's
 * `Tag` renders, so nothing announced it; carrying the sentence as the tag's own
 * content is what makes it reachable without hovering.
 */
const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Props for AttachmentList component
 */
interface AttachmentListProps {
  /** List of files to display */
  fileList: (UploadFile & {
    size?: number;
    userNotes?: string;
  })[];
  /** Map of all attachments */
  attachments: ArtifactAttachments;
  /** Path of the artifact these attachments hang off, which scopes the version history */
  artifactPath: string;
  /** Whether any operation is currently in progress */
  anyOperationInProgress: boolean;
  /** Whether the component is disabled */
  disabled: boolean;
  /** Handler for downloading a file */
  onDownload: (fileUid: string, fileName: string) => void;
  /** Handler for deleting a file (all versions) */
  onDelete: (fileUid: string, fileName: string) => void;
  /** Handler for deleting a specific version */
  onVersionDelete: (
    fileUid: string,
    versionId: string,
    versionNumber: number,
    fileName: string,
  ) => void;
  /** Handler for downloading a specific version */
  onVersionDownload: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for publishing the file's current save as a published version */
  onPublishVersion: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for sharing a version */
  onShareVersion: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for unsharing a version */
  onUnshareVersion: (fileUid: string, versionId: string) => Promise<void>;
  /** Handler for copying share link */
  onCopyShareLink: (fileUid: string, versionId: string) => Promise<void>;
  /** Handler for previewing a file version */
  onPreviewVersion?: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => void;
  /** Handler for emailing a file version */
  onEmailVersion?: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for renaming a file (all versions) */
  onRenameFile?: (
    fileUid: string,
    newFileName: string,
  ) => Promise<void | {
    success: boolean;
    error?: any;
    updatedAttachments?: ArtifactAttachments;
  }>;
  /** Handler for updating file notes */
  onUpdateFileNotes?: (fileUid: string, notes: string) => Promise<void>;
  /** Handler for updating file importance */
  onUpdateFileImportance?: (
    fileUid: string,
    importance: string,
  ) => Promise<void>;
  /** Handler for replacing the labels assigned to a file */
  onUpdateFileLabels?: (fileUid: string, labelIds: LabelId[]) => Promise<void>;
  /** Optional context used to build human-readable names for raw email source files */
  rawEmailAliasContext?: RawEmailAliasContext;
  /** The file row a file drag is currently over, or null for the panel background */
  dropTargetFileUid?: string | null;
  /** Lets a row claim the pointer, from `useAttachmentPanelDrop`; absent when the panel takes no drops */
  onRowDragEnter?: (fileUid: string, event: DragEvent<HTMLElement>) => void;
  /** Releases the claim the matching `onRowDragEnter` made */
  onRowDragLeave?: (fileUid: string, event: DragEvent<HTMLElement>) => void;
  /** The panel's host, which decides whether Extract schedule can write to a form. */
  extractScheduleHost: ExtractScheduleHost;
  /** The user holds aiOperator or aiAdmin. */
  canExtractSchedule: boolean;
  /** The version an extraction is reading, or null. */
  extractingVersionId: string | null;
  /** Handler for extracting a schedule from a version. */
  onExtractSchedule: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
}

/**
 * Why the file's current state cannot be published, or null when it can be.
 * Only a save is publishable, and absence of `kind` means published, so a
 * version predating desktop sync never offers publishing.
 */
const resolvePublishBlockedReason = (
  currentVersionId: string | undefined,
  currentVersion: AttachmentVersion | undefined,
  readOnly: boolean,
  busy: boolean,
): string | null => {
  if (!currentVersionId || !currentVersion) {
    return 'This file has no current version to publish.';
  }
  if (currentVersion.kind !== 'save') {
    return 'This file already points at a published version.';
  }
  if (readOnly) {
    return 'This document is read-only, so nothing here can be published.';
  }
  if (busy) {
    return 'Wait for the running attachment operation to finish.';
  }
  return null;
};

/**
 * Renders the list of attachments.
 */
export const AttachmentList: FC<AttachmentListProps> = ({
  fileList,
  attachments,
  artifactPath,
  anyOperationInProgress,
  disabled,
  onDelete,
  onVersionDelete,
  onVersionDownload,
  onPublishVersion,
  onShareVersion,
  onUnshareVersion,
  onCopyShareLink,
  onPreviewVersion,
  onEmailVersion,
  onRenameFile,
  onUpdateFileNotes,
  onUpdateFileImportance,
  onUpdateFileLabels,
  rawEmailAliasContext,
  dropTargetFileUid = null,
  onRowDragEnter,
  onRowDragLeave,
  extractScheduleHost,
  canExtractSchedule,
  extractingVersionId,
  onExtractSchedule,
}) => {
  const { token } = useToken();
  const { isPdfExportMode } = usePdfExportContext();
  const hasFileSyncAccess = useFileSyncAccess();
  const transfersSnap = useSnapshot(attachmentTransfersState);
  const attachmentLabels = useAttachmentLabels();
  const retentionSentence = resolveAttachmentRetentionSentence(
    useAttachmentSaveRetentionDays(),
  );
  const [expandedVersions, setExpandedVersions] = useState<
    Record<string, boolean>
  >({});
  const versionHistory = useAttachmentVersionHistory(
    artifactPath,
    attachments,
    expandedVersions,
  );
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const [editingImportance, setEditingImportance] = useState<
    Record<string, any>
  >({});

  // Debounced functions to improve performance during typing
  const debouncedUpdateNotes = useMemo(
    () =>
      debounce((fileUid: string, notes: string) => {
        if (onUpdateFileNotes) {
          onUpdateFileNotes(fileUid, notes);
        }
      }, 500),
    [onUpdateFileNotes],
  );

  const debouncedUpdateImportance = useMemo(
    () =>
      debounce((fileUid: string, importance: string) => {
        if (onUpdateFileImportance) {
          onUpdateFileImportance(fileUid, importance);
        }
      }, 300),
    [onUpdateFileImportance],
  );

  /**
   * Defines the order of importance for sorting.
   */
  const importanceOrder: Record<string, number> = {
    high: 0,
    mid: 1,
    low: 2,
  };

  /**
   * Toggles the display of version history for a file
   */
  const toggleVersions = useCallback(
    (fileUid: string) => {
      if (anyOperationInProgress) return;

      setExpandedVersions((prev) => ({
        ...prev,
        [fileUid]: !prev[fileUid],
      }));
    },
    [anyOperationInProgress],
  );

  /**
   * Gets the most recent version ID for a file
   */
  const getLatestVersionId = useCallback(
    (fileUid: string) => {
      const attachmentData = attachments[fileUid];
      if (
        !attachmentData ||
        !attachmentData.versions ||
        Object.keys(attachmentData.versions).length === 0
      ) {
        return null;
      }

      if (
        attachmentData.currentVersionId &&
        attachmentData.versions[attachmentData.currentVersionId]
      ) {
        return attachmentData.currentVersionId;
      }

      const versionEntries = Object.entries(attachmentData.versions);
      if (versionEntries.length === 0) return null;

      return versionEntries.reduce((latest, [currentId, currentVersion]) => {
        if (!latest) return currentId;
        const latestVersion = attachmentData.versions[latest];
        if (!latestVersion) return currentId;
        return currentVersion.versionNumber > latestVersion.versionNumber
          ? currentId
          : latest;
      }, versionEntries[0][0]);
    },
    [attachments],
  );

  /**
   * When each file last changed, keyed by fileUid: the moment the version its
   * row lists was written. The date column prints it and the sort below reads
   * it, so the order and the dates on screen can never disagree.
   */
  const changedAtByFileUid = Object.fromEntries(
    fileList.map((file) => {
      const fileAttachmentData = attachments[file.uid];
      const latestVersionId = getLatestVersionId(file.uid);
      return [
        file.uid,
        resolveAttachmentChangedAt(
          latestVersionId
            ? fileAttachmentData?.versions?.[latestVersionId]
            : undefined,
          fileAttachmentData,
        ),
      ];
    }),
  );

  /**
   * Sorts the file list by importance and then by when each file last changed,
   * so the file revised most recently leads its importance group. A file whose
   * date is unreadable sorts below every file that has one.
   */
  const sortedFileList = [...fileList].sort((a, b) => {
    const aImportance = attachments[a.uid]?.importance || 'mid';
    const bImportance = attachments[b.uid]?.importance || 'mid';

    if (importanceOrder[aImportance] !== importanceOrder[bImportance]) {
      return importanceOrder[aImportance] - importanceOrder[bImportance];
    }

    const aDate = changedAtByFileUid[a.uid];
    const bDate = changedAtByFileUid[b.uid];

    if (aDate && bDate) return bDate.toMillis() - aDate.toMillis();
    if (aDate) return -1;
    if (bDate) return 1;

    return 0;
  });

  /**
   * Every transfer, split by whether it already has a row to replace. Both
   * reads touch `fileUid` alone — the one leaf a transfer never rewrites — so
   * the list re-renders when a transfer starts or ends and at no other time,
   * a failure included. A file keeps every transfer it owns, because nothing
   * stops two of them existing over one fileUid.
   */
  const transferEntries = Object.entries(transfersSnap.transfers);
  const transferIdsByFileUid = transferEntries.reduce<Record<string, string[]>>(
    (byFileUid, [transferId, transfer]) =>
      transfer.fileUid === null
        ? byFileUid
        : {
            ...byFileUid,
            [transfer.fileUid]: [
              ...(byFileUid[transfer.fileUid] ?? []),
              transferId,
            ],
          },
    {},
  );
  const newFileTransferIds = transferEntries
    .filter(([, transfer]) => transfer.fileUid === null)
    .map(([transferId]) => transferId);

  /** The pin a file here may belong to. `null` for an artifact no pin can name. */
  const pinPath = resolveDesktopPinPath(artifactPath);

  /** The one row the desktop-sync nudge may appear on, or undefined for none. */
  const nudgeFileUid = sortedFileList.find(
    (file) => (file.size ?? 0) > LARGE_ATTACHMENT_ADVISORY_BYTES,
  )?.uid;

  if (fileList.length === 0 && newFileTransferIds.length === 0) {
    return null;
  }

  return (
    <FlexBox
      column
      gap={0}
      style={{
        maxWidth: `min(${MAX_LIMITED_WIDTH_VIEWPORT}px, var(${FORM_PAGE_CSS_VARS.formContentMaxWidth}, 100%))`,
        width: '100%',
        borderTop: `1px solid ${token.colorSplit}`,
      }}
      id="attachment-list-container"
    >
      {newFileTransferIds.map((transferId) => (
        <FlexCol
          key={transferId}
          gap={0}
          style={{
            borderBottom: `1px solid ${token.colorSplit}`,
            width: '100%',
          }}
          id={`attachment-list-new-file-${transferId}`}
        >
          <AttachmentTransferRow transferId={transferId} />
        </FlexCol>
      ))}
      {sortedFileList.map((file) => {
        const fileUid = file.uid;
        if (!fileUid) return null;

        const fileTransferIds = transferIdsByFileUid[fileUid] ?? [];

        const fileAttachmentData = attachments[fileUid];
        const expanded = !!(fileUid && expandedVersions[fileUid]);
        const latestVersionId = fileUid ? getLatestVersionId(fileUid) : null;
        const latestVersionDetails =
          latestVersionId && fileAttachmentData?.versions
            ? fileAttachmentData.versions[latestVersionId]
            : null;
        const sourceFileName = (
          resolveCurrentAttachmentName(fileAttachmentData) ||
          file.name ||
          ''
        ).trim();
        const displayFileName = resolveAttachmentDisplayName(
          sourceFileName,
          latestVersionDetails?.emailOriginRecordId,
          rawEmailAliasContext,
        );
        const fileHistory = versionHistory[fileUid];
        const versions = fileHistory?.versions ?? [];
        const artifactVersions = fileAttachmentData?.versions ?? {};
        const currentVersionId = fileAttachmentData?.currentVersionId;
        const currentVersionDetails = currentVersionId
          ? artifactVersions[currentVersionId]
          : undefined;
        const publishBlockedReason = resolvePublishBlockedReason(
          currentVersionId,
          currentVersionDetails,
          disabled,
          anyOperationInProgress,
        );
        const listedVersionCount =
          versions.length === 1 ? '1 version' : `${versions.length} versions`;
        const versionHistorySummary = fileHistory?.documentRead
          ? `The current version. The history lists ${listedVersionCount}.`
          : 'The current version. Expand this file to see its history.';
        const stripListsSaves = versions.some(
          ([, version]) => version.kind === 'save',
        );

        const takesDrop =
          fileTransferIds.length === 0 && onRowDragEnter !== undefined;
        const isDropTarget = takesDrop && dropTargetFileUid === fileUid;

        return (
          <FlexCol
            key={fileUid}
            gap={0}
            style={{
              borderBottom: `1px solid ${token.colorSplit}`,
              width: '100%',
              backgroundColor: isDropTarget
                ? token.controlItemBgActive
                : undefined,
              outline: isDropTarget
                ? `${token.lineWidth}px dashed ${token.colorPrimary}`
                : undefined,
              outlineOffset: isDropTarget ? -token.lineWidth : undefined,
            }}
            onDragEnter={
              takesDrop
                ? (event) => onRowDragEnter?.(fileUid, event)
                : undefined
            }
            onDragLeave={
              takesDrop
                ? (event) => onRowDragLeave?.(fileUid, event)
                : undefined
            }
            id={`attachment-list-item-${fileUid}`}
          >
            {fileTransferIds.length > 0 ? (
              fileTransferIds.map((fileTransferId) => (
                <AttachmentTransferRow
                  key={fileTransferId}
                  transferId={fileTransferId}
                />
              ))
            ) : (
              <FlexBox
                stretch
                wrap
                style={{
                  padding: 5,
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <AttachmentFileTypeIcon
                  fileName={sourceFileName}
                  declaredContentType={latestVersionDetails?.type}
                />
                <Text
                  style={{
                    width: 300,
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                  }}
                  ellipsis
                  editable={
                    disabled || anyOperationInProgress
                      ? false
                      : {
                          tooltip: 'Edit name',
                          onChange: async (newFileName) => {
                            if (!fileUid || !onRenameFile) return;

                            const trimmedName = newFileName.trim();

                            if (newFileName !== '' && trimmedName === '') {
                              return;
                            }

                            if (
                              trimmedName === sourceFileName &&
                              newFileName !== ''
                            ) {
                              return;
                            }

                            try {
                              await onRenameFile(fileUid, newFileName);
                            } catch (error) {
                              console.error(
                                'Unexpected error during rename in AttachmentList:',
                                error,
                              );
                            }
                          },
                        }
                  }
                  onClick={(e) => {
                    if (disabled || anyOperationInProgress) {
                      e.preventDefault();
                    }
                  }}
                  aria-label={`File name ${displayFileName}, click to edit`}
                >
                  {displayFileName}
                </Text>
                <Text
                  type="secondary"
                  style={{
                    whiteSpace: 'nowrap',
                    width: 75,
                    paddingLeft: 5,
                  }}
                  ellipsis
                >
                  {file.size ? `${formatBytes(file.size)}` : ''}
                </Text>
                <Text
                  type="secondary"
                  style={{
                    whiteSpace: 'nowrap',
                    width: 200,
                    maxWidth: '100%',
                  }}
                  ellipsis
                >
                  {fileUid &&
                  fileAttachmentData &&
                  latestVersionId &&
                  latestVersionDetails
                    ? formatAttachmentDate(changedAtByFileUid[fileUid])
                    : ''}
                </Text>
                <LabelTags
                  set={attachmentLabels}
                  labelIds={fileAttachmentData?.labelIds}
                />
                {fileUid && fileAttachmentData && latestVersionDetails && (
                  <AttachmentListActions
                    fileUid={fileUid}
                    versionId={latestVersionId as string}
                    fileName={displayFileName}
                    version={latestVersionDetails}
                    inArtifactMap
                    isCurrentVersion={latestVersionId === currentVersionId}
                    anyOperationInProgress={anyOperationInProgress}
                    disabled={disabled}
                    onPreviewVersion={onPreviewVersion}
                    onVersionDownload={onVersionDownload}
                    onShareVersion={onShareVersion}
                    onUnshareVersion={onUnshareVersion}
                    onCopyShareLink={onCopyShareLink}
                    onEmailVersion={onEmailVersion}
                    extractScheduleHost={extractScheduleHost}
                    canExtractSchedule={canExtractSchedule}
                    extractingVersionId={extractingVersionId}
                    onExtractSchedule={onExtractSchedule}
                  />
                )}
                {fileUid && fileAttachmentData && (
                  <Tooltip
                    title={
                      publishBlockedReason ??
                      'Publish this save. Publishing is permanent and cannot be undone.'
                    }
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <Popconfirm
                        title="Publish this save"
                        description={
                          <Text>
                            This save becomes a published version everyone
                            reading this document can find. Publishing is
                            permanent and cannot be undone.
                          </Text>
                        }
                        okText="Publish"
                        cancelText="Cancel"
                        disabled={publishBlockedReason !== null}
                        onConfirm={() => {
                          if (!currentVersionId) return;
                          void onPublishVersion(
                            fileUid,
                            currentVersionId,
                            displayFileName,
                          );
                        }}
                      >
                        <Button
                          size="small"
                          type="text"
                          icon={<CloudUploadOutlined />}
                          disabled={publishBlockedReason !== null}
                          id={`attachment-publish-button-${fileUid}`}
                          aria-label={`Publish ${displayFileName}`}
                        />
                      </Popconfirm>
                    </span>
                  </Tooltip>
                )}
                {hasFileSyncAccess &&
                  fileUid &&
                  fileAttachmentData &&
                  !isPdfExportMode && (
                    <AttachmentFileKeepToggle
                      fileUid={fileUid}
                      fileName={displayFileName}
                      pinPath={pinPath}
                    />
                  )}
                {currentVersionDetails && (
                  <Tooltip
                    title={versionHistorySummary}
                    trigger={['hover', 'focus']}
                  >
                    <Tag
                      variant="filled"
                      style={{ margin: 0 }}
                      id={`attachment-list-current-version-${fileUid}`}
                      tabIndex={0}
                    >
                      {formVersionNumberPrefix}
                      {currentVersionDetails.versionNumber}
                      <span style={srOnly}>{` ${versionHistorySummary}`}</span>
                    </Tag>
                  </Tooltip>
                )}
                {(() => {
                  if (!latestVersionDetails) return null;
                  const syncChip =
                    resolveAttachmentSyncChip(latestVersionDetails);
                  if (!syncChip) return null;
                  return (
                    <Tag
                      style={{ margin: 0 }}
                      id={`attachment-list-synced-${fileUid}`}
                    >
                      {syncChip}
                    </Tag>
                  );
                })()}
                {(() => {
                  if (!latestVersionDetails) return null;
                  const origin = resolveAttachmentVersionOrigin(
                    fileAttachmentData?.meta as
                      Record<string, unknown> | undefined,
                    latestVersionDetails,
                  );
                  if (origin === 'uploadedHere') return null;
                  return (
                    <Tag
                      style={{ margin: 0 }}
                      id={`attachment-list-origin-${fileUid}`}
                    >
                      {attachmentVersionOriginLabels[origin]}
                    </Tag>
                  );
                })()}
                {fileUid && onUpdateFileNotes && (
                  <FlexBox>
                    <Input
                      id={`attachment-list-notes-${fileUid}`}
                      value={
                        editingNotes[fileUid] !== undefined
                          ? editingNotes[fileUid]
                          : fileAttachmentData?.userNotes || ''
                      }
                      onChange={(e) => {
                        const newNotes = e.target.value;
                        setEditingNotes((prev) => ({
                          ...prev,
                          [fileUid]: newNotes,
                        }));
                        debouncedUpdateNotes(fileUid, newNotes);
                      }}
                      placeholder="Add notes..."
                      disabled={anyOperationInProgress || disabled}
                      aria-label={`User notes for ${displayFileName}`}
                      variant="filled"
                    />
                    {fileUid && onUpdateFileImportance && (
                      <Select
                        labelInValue
                        value={
                          ImportanceOptions.find(
                            (opt) =>
                              opt.value ===
                              (editingImportance[fileUid]?.value ||
                                fileAttachmentData?.importance),
                          ) ?? undefined
                        }
                        onChange={(option) => {
                          const selectedOption = option;
                          setEditingImportance((prev) => ({
                            ...prev,
                            [fileUid]: selectedOption,
                          }));
                          debouncedUpdateImportance(
                            fileUid,
                            selectedOption.value,
                          );
                        }}
                        disabled={anyOperationInProgress || disabled}
                        aria-label={`Importance for ${displayFileName}`}
                        variant="borderless"
                        style={{
                          width: 80,
                          padding: 0,
                        }}
                        styles={{
                          popup: {
                            root: {
                              width: 120,
                            },
                          },
                        }}
                        options={ImportanceOptions}
                        optionRender={(option) => {
                          return (
                            <Tag
                              color={option.data.color}
                              style={{ width: 50 }}
                              variant="filled"
                            >
                              {option.data.label}
                            </Tag>
                          );
                        }}
                        labelRender={(option) => {
                          const importanceOption = ImportanceOptions.find(
                            (opt) => opt.value === option.value,
                          );
                          return (
                            <Tag
                              color={importanceOption?.color}
                              style={{ width: 50 }}
                              variant="filled"
                            >
                              {option.label}
                            </Tag>
                          );
                        }}
                        suffixIcon={null}
                      />
                    )}
                  </FlexBox>
                )}
                {fileUid && onUpdateFileLabels && (
                  <AttachmentLabelPicker
                    set={attachmentLabels}
                    fileName={displayFileName}
                    labelIds={fileAttachmentData?.labelIds}
                    disabled={anyOperationInProgress || disabled}
                    onChange={(labelIds) => {
                      void onUpdateFileLabels(fileUid, labelIds);
                    }}
                  />
                )}
                <Button
                  type="text"
                  onClick={() => fileUid && toggleVersions(fileUid)}
                  disabled={anyOperationInProgress || !fileUid}
                  id={`attachment-list-toggle-versions-button-${fileUid}`}
                  aria-label={
                    expanded
                      ? `Collapse version history for ${displayFileName}`
                      : `Expand version history for ${displayFileName}`
                  }
                  icon={expanded ? <DownOutlined /> : <RightOutlined />}
                />
              </FlexBox>
            )}

            {hasFileSyncAccess &&
              fileUid === nudgeFileUid &&
              fileTransferIds.length === 0 &&
              !isPdfExportMode && (
                <AttachmentSyncNudge
                  fileUid={fileUid}
                  fileName={displayFileName}
                  sizeBytes={file.size}
                />
              )}

            {fileUid && expanded && fileAttachmentData && (
              <FlexCol
                gap={5}
                stretch
                style={{
                  backgroundColor: token.colorBgContainerDisabled,
                  padding: 10,
                }}
                id={`attachment-list-versions-${fileUid}`}
              >
                {versions.length === 0 && (
                  <Text
                    type="secondary"
                    id={`attachment-list-no-versions-${fileUid}`}
                  >
                    This file holds no versions.
                  </Text>
                )}
                {versions.map(([versionId, versionDetails], i) => (
                  <AttachmentVersionRow
                    key={versionId}
                    fileUid={fileUid}
                    versionId={versionId}
                    version={versionDetails}
                    isEvenRow={i % 2 === 0}
                    isCurrentVersion={versionId === currentVersionId}
                    fallbackFileName={file.name}
                    attachmentMeta={
                      fileAttachmentData?.meta as
                        Record<string, unknown> | undefined
                    }
                    inArtifactMap={artifactVersions[versionId] !== undefined}
                    anyOperationInProgress={anyOperationInProgress}
                    disabled={disabled}
                    rawEmailAliasContext={rawEmailAliasContext}
                    onPreviewVersion={onPreviewVersion}
                    onVersionDownload={onVersionDownload}
                    onShareVersion={onShareVersion}
                    onUnshareVersion={onUnshareVersion}
                    onCopyShareLink={onCopyShareLink}
                    onEmailVersion={onEmailVersion}
                    onVersionDelete={onVersionDelete}
                    extractScheduleHost={extractScheduleHost}
                    canExtractSchedule={canExtractSchedule}
                    extractingVersionId={extractingVersionId}
                    onExtractSchedule={onExtractSchedule}
                  />
                ))}
                {retentionSentence && stripListsSaves && (
                  <Text
                    type="secondary"
                    id={`attachment-list-retention-note-${fileUid}`}
                  >
                    {retentionSentence}
                  </Text>
                )}
                <FlexBox justifyEnd stretch>
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      fileUid && onDelete(fileUid, displayFileName)
                    }
                    disabled={disabled || anyOperationInProgress || !fileUid}
                    style={{
                      marginTop: 15,
                      marginBottom: 10,
                    }}
                    size="small"
                    id={`attachment-list-delete-all-versions-button-${fileUid}`}
                    aria-label={`Delete ${displayFileName} with every published version and save it holds`}
                  >
                    Delete File and Every Version
                  </Button>
                </FlexBox>
              </FlexCol>
            )}
          </FlexCol>
        );
      })}
    </FlexBox>
  );
};
